import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';

// Create context for dropdown
const DropdownContext = createContext();

// Hook to use dropdown context
const useDropdown = () => {
	const context = useContext(DropdownContext);
	if (!context) {
		throw new Error('DropdownItem must be used within a Dropdown');
	}
	return context;
};

/**
 * Dropdown - UI primitive for dropdown menus
 * 
 * A flexible dropdown component that can be used for action menus,
 * select dropdowns, and other dropdown interfaces.
 */
export const Dropdown = ({
	trigger,
	children,
	align = 'right',
	className,
	onOpenChange,
	...props
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [coords, setCoords] = useState(null);
	const dropdownRef = useRef(null);
	const menuRef = useRef(null);

	const closeDropdown = () => {
		setIsOpen(false);
		onOpenChange?.(false);
	};

	/**
	 * Position against the viewport, because the menu is PORTALED to <body>.
	 *
	 * It used to be an absolutely-positioned child of the trigger, which meant
	 * any ancestor that clips - notably the tables' `overflow-x-auto`, since CSS
	 * forces overflow-y to auto when either axis is non-visible - cut the menu
	 * off. On a table with two rows the actions menu was mostly invisible.
	 *
	 * Sides are expressed as left/right offsets so the menu never has to be
	 * measured horizontally; only the flip-up check needs its height.
	 */
	const position = () => {
		const trigger = dropdownRef.current;
		if (!trigger) return;
		const r = trigger.getBoundingClientRect();
		const menuH = menuRef.current?.offsetHeight || 0;
		const flipUp = menuH > 0 && r.bottom + menuH + 8 > window.innerHeight;

		const next = { top: flipUp ? undefined : r.bottom + 4, bottom: flipUp ? window.innerHeight - r.top + 4 : undefined };
		if (align === 'right') next.right = window.innerWidth - r.right;
		else if (align === 'center') { next.left = r.left + r.width / 2; next.translate = true; }
		else next.left = r.left;
		setCoords(next);
	};

	useEffect(() => {
		if (!isOpen) { setCoords(null); return; }
		position();
		// Re-measure once the menu has rendered, so the flip-up decision uses a
		// real height rather than 0.
		const raf = requestAnimationFrame(position);

		const onDismiss = (event) => {
			// The menu lives outside dropdownRef now, so it must be checked too -
			// otherwise mousedown on a menu item closes before its click fires.
			if (dropdownRef.current?.contains(event.target)) return;
			if (menuRef.current?.contains(event.target)) return;
			closeDropdown();
		};
		const onKey = (event) => { if (event.key === 'Escape') closeDropdown(); };

		document.addEventListener('mousedown', onDismiss);
		document.addEventListener('keydown', onKey);
		// Capture phase: catch scrolling inside any container, not just the page.
		window.addEventListener('scroll', position, true);
		window.addEventListener('resize', position);
		return () => {
			cancelAnimationFrame(raf);
			document.removeEventListener('mousedown', onDismiss);
			document.removeEventListener('keydown', onKey);
			window.removeEventListener('scroll', position, true);
			window.removeEventListener('resize', position);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, align]);

	const handleToggle = () => {
		const next = !isOpen;
		setIsOpen(next);
		onOpenChange?.(next);
	};

	return (
		<div className="relative inline-block" ref={dropdownRef} {...props}>
			<div onClick={handleToggle}>{trigger}</div>

			{isOpen && createPortal(
				<DropdownContext.Provider value={{ closeDropdown }}>
					<div
						ref={menuRef}
						role="menu"
						style={{
							position: 'fixed',
							top: coords?.top,
							bottom: coords?.bottom,
							left: coords?.left,
							right: coords?.right,
							transform: coords?.translate ? 'translateX(-50%)' : undefined,
							visibility: coords ? 'visible' : 'hidden',
						}}
						className={cn(
							// A `position: fixed` box with `right` set but no `left` and no
							// width shrinks to fit against the VIEWPORT, not the trigger.
							// Any consumer whose content has a large max-content width
							// therefore grew to fill the screen - and `truncate`
							// (white-space: nowrap) makes max-content the entire unwrapped
							// string, so it inflated the panel instead of clipping inside
							// it. That is how the notification bell rendered as a ~1300px
							// slab pinned to the left edge of the window. The cap belongs
							// here rather than in each consumer, so no future menu can
							// reintroduce it; a consumer needing a different width still
							// wins, because cn() is tailwind-merge.
							'z-[60] min-w-[180px] max-w-[min(24rem,calc(100vw-1.5rem))] max-h-[70vh] overflow-y-auto bg-card rounded-md border border-border shadow-lg',
							className
						)}
					>
						{children}
					</div>
				</DropdownContext.Provider>,
				document.body
			)}
		</div>
	);
};

/**
 * DropdownItem - Individual item in dropdown menu
 */
export const DropdownItem = ({
	children,
	onClick,
	disabled = false,
	className,
	...props
}) => {
	const { closeDropdown } = useDropdown();

	const handleClick = () => {
		if (!disabled && onClick) {
			onClick();
			// Close the dropdown after clicking an item
			closeDropdown();
		}
	};

	return (
		<button
			className={cn(
				"w-full px-3 py-2 text-left text-sm hover:bg-muted first:rounded-t-md last:rounded-b-md transition-colors",
				disabled && "opacity-50 cursor-not-allowed",
				className
			)}
			onClick={handleClick}
			disabled={disabled}
			{...props}
		>
			{children}
		</button>
	);
};

/**
 * DropdownSeparator - Separator line in dropdown menu
 */
export const DropdownSeparator = ({ className, ...props }) => (
	<div
		className={cn("h-px bg-accent my-1", className)}
		{...props}
	/>
);

export const dropdownVariants = {
	align: {
		left: 'left-0',
		right: 'right-0',
		center: 'left-1/2 -translate-x-1/2'
	}
}; 